import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Button,
    Form,
    Dropdown,
    ButtonGroup,
    Modal,
    Card,
} from "react-bootstrap";
import { MdAddchart } from "react-icons/md";
import { Link } from "react-router-dom";
import "./interview.css";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaEdit } from "react-icons/fa";
import { IoIosEye } from "react-icons/io";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function InterviewerData() {
    const rowsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const [interviewers, setInterviewers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedInterviewer, setSelectedInterviewer] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [interviewerToDelete, setInterviewerToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: "",
        age: "",
        address: "",
        phone: "",
        email: "",
        notes: "",
    });

    // Fetch interviewers from backend
    const fetchInterviewers = async () => {
        try {
            const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/interviewers");
            if (response.data.success) {
                setInterviewers(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching interviewers:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initialize with data from backend
    useEffect(() => {
        fetchInterviewers();
    }, []);

    // Filter interviewers based on search query
    const filteredInterviewers = interviewers.filter((interviewer) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            interviewer.name.toLowerCase().includes(query) ||
            interviewer.email.toLowerCase().includes(query) ||
            interviewer.phone.toLowerCase().includes(query) ||
            interviewer.address.toLowerCase().includes(query)
        );
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredInterviewers.length / rowsPerPage);
    const paginatedInterviewers = filteredInterviewers.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // Calculate page range for pagination controls
    const getPageRange = () => {
        const pages = [];
        let startPage = Math.max(currentPage - 1, 1);
        let endPage = Math.min(startPage + 2, totalPages);

        if (endPage - startPage < 2) {
            startPage = Math.max(endPage - 2, 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    // View interviewer details
    const handleViewDetails = (interviewer) => {
        setSelectedInterviewer(interviewer);
        setShowDialog(true);
    };

    // Open edit modal and set form data
    const handleEdit = (interviewer) => {
        setSelectedInterviewer(interviewer);
        setEditFormData({
            name: interviewer.name,
            age: interviewer.age,
            address: interviewer.address,
            phone: interviewer.phone,
            email: interviewer.email,
            notes: interviewer.notes || "",
        });
        setShowEditModal(true);
    };

    // Handle form input changes
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle notes change for Quill editor
    const handleNotesChange = (value) => {
        setEditFormData((prev) => ({
            ...prev,
            notes: value,
        }));
    };

    // Submit edited data
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.put(
                `https://backend.ems.uk.hiradentallab.com/api/interviewers/${selectedInterviewer._id}`,
                editFormData
            );
            fetchInterviewers(); // Refresh the list
            setShowEditModal(false);
        } catch (error) {
            console.error("Error updating interviewer:", error);
        }
    };

    // Delete interviewer
    const handleDelete = async () => {
        try {
            await axios.delete(
                `https://backend.ems.uk.hiradentallab.com/api/interviewers/${interviewerToDelete._id}`
            );
            fetchInterviewers(); // Refresh the list after deletion
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting interviewer:", error);
        }
    };

    // Function to construct proper file URLs
    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        // If it's already a full URL, return as is
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
            return filePath;
        }
        // Otherwise construct the full URL
        return `https://backend.ems.uk.hiradentallab.com/${filePath.replace(/\\/g, '/')}`;
    };

    // Function to render document preview based on file type
    const renderDocumentPreview = (fileUrl) => {
        if (!fileUrl) return null;

        const url = getFileUrl(fileUrl);
        const extension = url.split('.').pop().toLowerCase();

        // Check if it's an image
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        if (imageExtensions.includes(extension)) {
            return (
                <div className="document-preview">
                    <img
                        src={url}
                        alt="Document Preview"
                        className="img-thumbnail"
                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                    />
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm mt-2"
                    >
                        View Full Image
                    </a>
                </div>
            );
        }

        // For PDF files
        if (extension === 'pdf') {
            return (
                <div className="document-preview">
                    <iframe
                        src={url}
                        title="Document Preview"
                        style={{ width: '100%', height: '200px', border: '1px solid #ddd' }}
                    />
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm mt-2"
                    >
                        Open Full PDF
                    </a>
                </div>
            );
        }

        // For other file types
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-sm"
            >
                View Document
            </a>
        );
    };

    return (
        <div className="container-fluid">
            <h1 className="emp-info-title">
                <BsFillInfoCircleFill className="settings-icon" /> Interviewee Information
            </h1>
            <hr />

            <h1 className="emp-info-subtitle">
                <CgProfile className="settings-icon" /> Interviewees
            </h1>
            <hr />

            <div className="emp-info-content">
                <Link to="/add-inteviewees-data">
                    <Button variant="primary" className="add-emp-button">
                        <MdAddchart className="button-icon" /> Add Interviewee
                    </Button>
                </Link>

                <div className="emp-info-actions">
                    <Row>
                        <Col xs={12} md={6} className="d-flex align-items-center mb-2">
                            <Dropdown as={ButtonGroup}>
                                <Button variant="primary">Show {rowsPerPage} Entries</Button>
                                <Dropdown.Toggle split variant="primary" />
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => {}}>10</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {}}>25</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {}}>50</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {}}>100</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>

                        <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
                            <Form className="d-flex" onSubmit={(e) => e.preventDefault()}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search Interviewer"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </Form>
                        </Col>
                    </Row>
                </div>

                <div className="table-responsive">
                    {loading ? (
                        <p>Loading interviewers...</p>
                    ) : (
                        <>
                            <table className="table table-striped table-style">
                                <thead className="tablehead">
                                <tr className="table-header-footer">
                                    <th className="text-center">#</th>
                                    <th className="text-center">Name</th>
                                    <th className="text-center">Age</th>
                                    <th className="text-center">Email</th>
                                    <th className="text-center">Phone</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedInterviewers.length > 0 ? (
                                    paginatedInterviewers.map((interviewer, index) => (
                                        <tr key={interviewer._id} style={{ textAlign: "center" }}>
                                            <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                                            <td>{interviewer.name}</td>
                                            <td>{interviewer.age}</td>
                                            <td>{interviewer.email}</td>
                                            <td>{interviewer.phone}</td>
                                            <td>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => handleViewDetails(interviewer)}
                                                >
                                                    <IoIosEye />
                                                </Button>
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => handleEdit(interviewer)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setInterviewerToDelete(interviewer);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    <RiDeleteBin5Line />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No interviewers found
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                                <tfoot className="table-footer">
                                <tr className="emp-info-table-footer">
                                    <td colSpan="5">Total Interviewers</td>
                                    <td>{filteredInterviewers.length}</td>
                                </tr>
                                </tfoot>
                            </table>

                            <Row className="mt-2">
                                <Col xs={12} md={6} className="d-flex align-items-center">
                                    <p className="showing-entries">
                                        Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                                        {Math.min(
                                            currentPage * rowsPerPage,
                                            filteredInterviewers.length
                                        )}{" "}
                                        of {filteredInterviewers.length} entries
                                    </p>
                                </Col>
                                <Col xs={12} md={6} className="d-flex justify-content-end">
                                    <nav>
                                        <ul className="pagination">
                                            <li
                                                className={`page-item ${
                                                    currentPage === 1 ? "disabled" : ""
                                                }`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            {getPageRange().map((page) => (
                                                <li
                                                    key={page}
                                                    className={`page-item ${
                                                        currentPage === page ? "active" : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            ))}
                                            <li
                                                className={`page-item ${
                                                    currentPage === totalPages ? "disabled" : ""
                                                }`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            </div>

            {/* Interviewer Details Modal */}
            <Modal
                show={showDialog}
                onHide={() => setShowDialog(false)}
                size="lg"
                centered
            >
                <Modal.Header
                    closeButton
                    style={{ backgroundColor: "#004aad", color: "white" }}
                >
                    <Modal.Title>Interviewer Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedInterviewer && (
                        <div>
                            {/* Basic Information Section */}
                            <div className="mb-4">
                                <h5>Basic Information</h5>
                                <div>
                                    <p>
                                        <strong>Name:</strong> {selectedInterviewer.name}
                                    </p>
                                    <p>
                                        <strong>Age:</strong> {selectedInterviewer.age}
                                    </p>
                                    <p>
                                        <strong>Phone:</strong> {selectedInterviewer.phone}
                                    </p>
                                    <p>
                                        <strong>Address:</strong> {selectedInterviewer.address}
                                    </p>
                                    <p>
                                        <strong>Email:</strong> {selectedInterviewer.email}
                                    </p>
                                    <p>
                                        <strong>Notes:</strong>
                                    </p>
                                    <div
                                        className="ql-editor"
                                        dangerouslySetInnerHTML={{
                                            __html: selectedInterviewer.notes || "N/A"
                                        }}
                                    />
                                    <p>
                                        <strong>Added On:</strong>{" "}
                                        {new Date(
                                            selectedInterviewer.createdAt
                                        ).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>

                            {/* Documents Section */}
                            <div>
                                <h5>Documents</h5>
                                <div className="row">
                                    {/* ID Front */}
                                    {selectedInterviewer.idFront && (
                                        <div className="col-md-6 mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <Card.Title style={{ fontSize: "1rem" }}>
                                                        ID Front
                                                    </Card.Title>
                                                    {renderDocumentPreview(selectedInterviewer.idFront)}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}

                                    {/* ID Back */}
                                    {selectedInterviewer.idBack && (
                                        <div className="col-md-6 mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <Card.Title style={{ fontSize: "1rem" }}>
                                                        ID Back
                                                    </Card.Title>
                                                    {renderDocumentPreview(selectedInterviewer.idBack)}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}

                                    {/* Interview Questions */}
                                    {selectedInterviewer.interviewQuestions && (
                                        <div className="col-md-6 mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <Card.Title style={{ fontSize: "1rem" }}>
                                                        Interview Questions
                                                    </Card.Title>
                                                    {renderDocumentPreview(selectedInterviewer.interviewQuestions)}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}

                                    {/* Educational Documents */}
                                    {selectedInterviewer.educationalDocs && (
                                        <div className="col-md-6 mb-3">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <Card.Title style={{ fontSize: "1rem" }}>
                                                        Educational Documents
                                                    </Card.Title>
                                                    {renderDocumentPreview(selectedInterviewer.educationalDocs)}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}

                                    {!selectedInterviewer.idFront &&
                                        !selectedInterviewer.idBack &&
                                        !selectedInterviewer.interviewQuestions &&
                                        !selectedInterviewer.educationalDocs && (
                                            <div className="col-12">
                                                <p>No documents available</p>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDialog(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Interviewer Modal */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="lg"
            >
                <Modal.Header
                    closeButton
                    style={{ backgroundColor: "#004aad", color: "white" }}
                >
                    <Modal.Title>Edit Interviewer</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={editFormData.name}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Age</Form.Label>
                            <Form.Control
                                type="number"
                                name="age"
                                value={editFormData.age}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                value={editFormData.address}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={editFormData.phone}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={editFormData.email}
                                onChange={handleEditFormChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={editFormData.notes}
                                onChange={handleNotesChange}
                                modules={{
                                    toolbar: [
                                        [{ 'header': [1, 2, false] }],
                                        ['bold', 'italic', 'underline', 'strike'],
                                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                        ['link', 'image'],
                                        ['clean']
                                    ]
                                }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete interviewer:{" "}
                    <strong>{interviewerToDelete?.name}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}